import * as React from 'react';
import {
  Theme,
  Paper,
  Chip,
  Grid,
  Typography,
  Button as Btn,
  WithStyles,
  withStyles,
  createStyles,
} from '@material-ui/core';
import { useQuery, useMutation } from 'react-apollo';
import { ModalTrigger } from 'contexts/ModalTrigger';
import CustomToast from 'shared/components/CustomToast';
import { SsoConnection, TrintGroup } from 'types';
import { ORGANISATION_QUERY } from 'organisations/queries';
import {
  SSO_CONNECTIONS_QUERY,
  SsoConnectionsQueryResult,
  SsoConnectionsQueryVariables,
} from 'organisations/sso/queries';
import { CREATE_SSO_PLACEHOLDER_CONNECTION } from 'organisations/sso/mutations';

// https://github.com/mui-org/material-ui/issues/11968
const Button: React.ComponentType<any> = Btn;

interface Props extends WithStyles<typeof styles> {
  groupId: string;
}

const fieldStyles = (theme: Theme) =>
  createStyles({
    field: { marginBottom: theme.spacing.unit * 1.5 },
    label: { fontWeight: 600 },
    value: { wordBreak: 'break-all' as const },
    emphasis: { fontWeight: 700 },
  });

interface FieldProps extends WithStyles<typeof fieldStyles> {
  label: string;
  value: string;
  /** Render the value in bold to draw attention to it. */
  emphasis?: boolean;
}

const Field = withStyles(fieldStyles)(
  ({ label, value, emphasis, classes }: FieldProps) => (
    <div className={classes.field}>
      <Typography variant="caption" className={classes.label}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        className={`${classes.value} ${emphasis ? classes.emphasis : ''}`}
      >
        {value}
      </Typography>
    </div>
  ),
);

const yesNo = (value: boolean | null | undefined) => (value ? 'yes' : 'no');

const listOrNone = (values: string[] | null | undefined) =>
  values && values.length ? values.join(', ') : 'none';

interface SettingsPanelProps {
  groupId: string;
  classes: Props['classes'];
}

interface OrganisationQueryRes {
  group: Pick<TrintGroup, 'connectionIds' | 'settings'>;
}

/**
 * Read-only summary of the organisation's SSO related settings. These
 * come from the group document rather than Auth0, so they are shown
 * separately from the connection cards.
 */
function SsoSettingsPanel({ groupId, classes }: SettingsPanelProps) {
  const { data, loading, error } = useQuery<
    OrganisationQueryRes,
    { groupId: string }
  >(ORGANISATION_QUERY, {
    variables: { groupId },
    fetchPolicy: 'cache-and-network',
  });

  const group = data && data.group;
  const connectionIds = (group && group.connectionIds) || [];
  const hasConnections = connectionIds.length > 0;

  // SSO only works when the organisation has at least one Auth0 connection
  // AND it has not been explicitly disabled. `disableSso` alone is not enough
  // to say SSO is enabled.
  let ssoStatus = 'enabled';
  if (group && group.settings.disableSso) {
    ssoStatus = 'DISABLED - SSO is disabled for this customer';
  } else if (!hasConnections) {
    ssoStatus = 'not configured - no Auth0 connections';
  }
  const ssoEnabled = ssoStatus === 'enabled';

  return (
    <Paper className={classes.paper}>
      <Typography component="h1" variant="h5" className={classes.title}>
        SSO Settings
      </Typography>

      {error && (
        <Typography color="error">
          Failed to load organisation settings: {error.message}
        </Typography>
      )}

      {!error && !group && loading && (
        <Typography className={classes.empty}>Loading...</Typography>
      )}

      {group && (
        <Grid container spacing={16}>
          <Grid item xs={12} sm={6}>
            <Field
              label="Single sign-on"
              value={ssoStatus}
              emphasis={!!group.settings.disableSso}
            />
            <Field
              label="Home realm discovery (HRD)"
              value={
                ssoEnabled && !group.settings.disableHrd ? 'enabled' : 'disabled'
              }
            />
            <Field
              label="Auto provisioning"
              value={yesNo(group.settings.autoProvision)}
            />
            <Field
              label="Password reset"
              value={
                group.settings.disablePasswordReset ? 'disabled' : 'allowed'
              }
            />
            <Field
              label="Auth server"
              value={group.settings.authServer || 'not set'}
            />
            <Field
              label="Bypass owned domain restriction (SCIM testing only)"
              value={yesNo(group.settings.bypassOwnedDomainRestriction)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field
              label="Auth0 connection IDs"
              value={listOrNone(connectionIds)}
            />
            <Field
              label="SSO domains"
              value={listOrNone(group.settings.ssoDomains)}
            />
            <Field
              label="Exempt from SSO"
              value={listOrNone(group.settings.exemptFromSso)}
            />
            <Field
              label="Restrict invites to SSO domains"
              value={yesNo(group.settings.enableDomainRestriction)}
            />
            <Field
              label="Allowed external domains"
              value={listOrNone(group.settings.allowedExternalDomains)}
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

function ConnectionCard({
  connection,
  classes,
}: {
  connection: SsoConnection;
  classes: Props['classes'];
}) {
  return (
    <Paper className={classes.card}>
      <div className={classes.cardHeader}>
        <Typography variant="h6" className={classes.value}>
          {connection.connectionId}
        </Typography>
        {connection.hasPlaceholderCert !== false && (
          <Chip
            label="Placeholder cert"
            color="secondary"
            className={classes.chip}
          />
        )}
      </div>

      <Field label="Entity ID" value={connection.entityId} />
      <Field label="ACS URL" value={connection.acsUrl} />
      <Field label="SSO Login URL" value={connection.ssoLoginUrl} />
      <Field
        label="Sign In URL (from Auth0)"
        value={connection.signInUrl || 'not set'}
      />

      <Button
        variant="outlined"
        color="primary"
        className={classes.testButton}
        onClick={() => window.open(connection.ssoLoginUrl, '_blank')}
      >
        Test config
      </Button>
    </Paper>
  );
}

function SsoConfigTab({ groupId, classes }: Props) {
  const { data, loading, error, refetch } = useQuery<
    SsoConnectionsQueryResult,
    SsoConnectionsQueryVariables
  >(SSO_CONNECTIONS_QUERY, {
    variables: { groupId },
    fetchPolicy: 'cache-and-network',
  });

  const [createPlaceholder, createResult] = useMutation(
    CREATE_SSO_PLACEHOLDER_CONNECTION,
    {
      variables: { groupId },
      onCompleted: () => refetch(),
    },
  );

  const connections = (data && data.ssoConnections) || [];

  return (
    <main className={classes.layout}>
      <SsoSettingsPanel groupId={groupId} classes={classes} />

      <Paper className={classes.paper}>
        <div className={classes.header}>
          <Typography component="h1" variant="h5">
            SSO Connections
          </Typography>
          <ModalTrigger<'ConfirmDialog'>
            modal="ConfirmDialog"
            modalProps={{
              title: 'Create a placeholder SSO config?',
              message:
                'This will create a new SSO connection in Auth0 and in the ' +
                'database for this organisation. It cannot be deleted from ' +
                'Unicorn; removing it requires manual changes in both Auth0 ' +
                'and the database. Are you sure you want to continue?',
              onConfirm: () => createPlaceholder(),
            }}
          >
            {({ openModal }) => (
              <Button
                variant="contained"
                color="primary"
                onClick={openModal}
                disabled={createResult.loading}
              >
                Create Placeholder Config
              </Button>
            )}
          </ModalTrigger>
        </div>

        {error && (
          <Typography color="error">
            Failed to load SSO connections: {error.message}
          </Typography>
        )}

        {!error && !connections.length && !loading && (
          <Typography className={classes.empty}>
            No SSO connections yet. Create a placeholder config to get started.
          </Typography>
        )}

        {connections.map((connection) => (
          <ConnectionCard
            key={connection.connectionId}
            connection={connection}
            classes={classes}
          />
        ))}
      </Paper>

      {createResult.error && (
        <CustomToast message={createResult.error.message} isOpen />
      )}
    </main>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    layout: {
      width: 'auto',
      marginLeft: theme.spacing.unit * 2,
      marginRight: theme.spacing.unit * 2,
      [theme.breakpoints.up(800 + theme.spacing.unit * 2 * 2)]: {
        width: 800,
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
    paper: {
      marginTop: theme.spacing.unit * 3,
      marginBottom: theme.spacing.unit * 3,
      padding: theme.spacing.unit * 3,
    },
    title: {
      marginBottom: theme.spacing.unit * 3,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.unit * 3,
    },
    empty: {
      color: theme.palette.text.secondary,
    },
    card: {
      padding: theme.spacing.unit * 2,
      marginBottom: theme.spacing.unit * 2,
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.unit * 2,
    },
    chip: {
      marginLeft: theme.spacing.unit,
    },
    value: {
      wordBreak: 'break-all',
    },
    testButton: {
      marginTop: theme.spacing.unit,
    },
  });

export default withStyles(styles)(SsoConfigTab);
